import { api } from '@/lib/api';
import { z } from 'zod';

export const buddyProfileSchema = z.object({
  name: z.string().trim().min(1).max(12),
  character: z.enum(['tuantuan', 'plush']),
  hat: z.enum(['none', 'cap', 'beanie', 'crown', 'beret', 'sailor', 'party']),
  earStyle: z.enum(['up', 'asymmetric', 'down']),
  room: z.enum(['cream', 'sky', 'mint']),
  accessories: z
    .array(z.enum(['satchel', 'scarf', 'backpack', 'bow', 'pendant', 'moonbag']))
    .max(6),
});
export type BuddyProfile = z.infer<typeof buddyProfileSchema>;
export const memoryInputSchema = z.object({
  text: z.string().trim().min(1).max(100),
  category: z.enum(['interest', 'preference', 'goal']),
});
export type MemoryInput = z.infer<typeof memoryInputSchema>;
export type BuddyMemory = MemoryInput & {
  id: string;
  source: 'explicit' | 'conversation';
  created_at: string;
  updated_at: string;
  forgotten_at: string | null;
};
export interface BuddySnapshot {
  revision: number;
  memory_enabled: boolean;
  profile: BuddyProfile | null;
  memories: BuddyMemory[];
  forgotten?: BuddyMemory[];
}
export interface BuddyMessage {
  id?: string;
  order_key?: string;
  role: 'user' | 'assistant';
  content: string;
  speech_token?: string;
}
export interface BuddyPageContext {
  page:
    | 'home'
    | 'design'
    | 'create'
    | 'story'
    | 'lights'
    | 'works'
    | 'work'
    | 'catalog'
    | 'study'
    | 'project'
    | 'studio'
    | 'lesson';
  title?: string;
  summary?: string;
}
export interface BuddyChatInput {
  language?: 'en' | 'zh';
  request_id: string;
  memory_revision: number;
  text: string;
  expected_stars: number;
  history: BuddyMessage[];
  pageContext: BuddyPageContext;
}
export interface BuddyVoiceSettings {
  revision: number;
  available: boolean;
  speech_enabled: boolean;
  transcription_enabled: boolean;
  voice: 'gentle' | 'playful' | 'calm';
  voices: Array<{ value: 'gentle' | 'playful' | 'calm'; label: string; description: string }>;
  max_recording_seconds: number;
}
export interface BuddyVoiceInput {
  language?: 'en' | 'zh';
  request_id: string;
  memory_revision: number;
  voice_revision: number;
}
export interface BuddyApi {
  history?(
    kidId: string,
    cursor?: { before?: string; after?: string },
    signal?: AbortSignal,
  ): Promise<{
    revision: number;
    messages: BuddyMessage[];
    has_earlier: boolean;
    has_later?: boolean;
    oldest_cursor?: string | null;
    newest_cursor?: string | null;
    cursor_reset?: boolean;
  }>;
  purge(kidId: string, revision: number, id: string): Promise<BuddySnapshot>;
  voice(kidId: string, parent?: boolean): Promise<BuddyVoiceSettings>;
  saveVoice(
    kidId: string,
    revision: number,
    value: Pick<BuddyVoiceSettings, 'speech_enabled' | 'transcription_enabled' | 'voice'>,
  ): Promise<BuddyVoiceSettings>;
  speak(
    kidId: string,
    input: BuddyVoiceInput & { speech_token?: string; sample?: 'hello' },
    signal: AbortSignal,
    parent?: boolean,
  ): Promise<{ audio_base64: string; mime_type: string }>;
  transcribe(
    kidId: string,
    input: BuddyVoiceInput & { audio_base64: string; confirmed: true },
    signal: AbortSignal,
  ): Promise<{ text: string }>;
  read(kidId: string, parent?: boolean): Promise<BuddySnapshot>;
  profile(kidId: string, revision: number, profile: BuddyProfile): Promise<BuddySnapshot>;
  settings(kidId: string, revision: number, enabled: boolean): Promise<BuddySnapshot>;
  remember(
    kidId: string,
    revision: number,
    id: string,
    memory: MemoryInput,
    parent?: boolean,
  ): Promise<BuddySnapshot>;
  forget(kidId: string, revision: number, id: string, parent?: boolean): Promise<BuddySnapshot>;
  restore(kidId: string, revision: number, id: string): Promise<BuddySnapshot>;
  price(kidId: string): Promise<{ stars: number }>;
  chat(
    kidId: string,
    input: BuddyChatInput,
    signal: AbortSignal,
  ): Promise<{ reply: string; speech_token?: string; snapshot?: BuddySnapshot }>;
}
const base = (kidId: string) => '/kids/' + encodeURIComponent(kidId) + '/companion';
export const companionApi: BuddyApi = {
  history: (id, cursor, signal) => {
    const params = new URLSearchParams(cursor);
    return api(base(id) + '/history' + (params.size ? '?' + params : ''), {
      principal: 'kid',
      ...(signal ? { signal } : {}),
    });
  },
  purge: (id, revision, memoryId) =>
    api(base(id) + '/memories/' + encodeURIComponent(memoryId) + '/purge', {
      principal: 'user',
      method: 'POST',
      body: { expected_revision: revision, confirmed: true },
    }),
  voice: (id, parent = false) => api(base(id) + '/voice', { principal: parent ? 'user' : 'kid' }),
  saveVoice: (id, revision, value) =>
    api(base(id) + '/voice', {
      principal: 'user',
      method: 'PUT',
      body: { expected_revision: revision, ...value, consent: true },
    }),
  speak: (id, input, signal, parent = false) =>
    api(base(id) + '/voice/speak', {
      principal: parent ? 'user' : 'kid',
      method: 'POST',
      body: input,
      signal,
    }),
  transcribe: (id, input, signal) =>
    api(base(id) + '/voice/transcribe', { principal: 'kid', method: 'POST', body: input, signal }),
  read: (id, parent = false) => api(base(id), { principal: parent ? 'user' : 'kid' }),
  profile: (id, revision, profile) =>
    api(base(id) + '/profile', {
      principal: 'kid',
      method: 'PUT',
      body: { expected_revision: revision, profile },
    }),
  settings: (id, revision, enabled) =>
    api(base(id) + '/settings', {
      principal: 'user',
      method: 'PATCH',
      body: { expected_revision: revision, memory_enabled: enabled },
    }),
  remember: (id, revision, memoryId, memory, parent = false) =>
    api(base(id) + '/memories/' + encodeURIComponent(memoryId), {
      principal: parent ? 'user' : 'kid',
      method: 'PUT',
      body: { ...memory, expected_revision: revision, confirmed: true },
    }),
  forget: (id, revision, memoryId, parent = false) =>
    api(base(id) + '/memories/' + encodeURIComponent(memoryId) + '/forget', {
      principal: parent ? 'user' : 'kid',
      method: 'POST',
      body: { expected_revision: revision },
    }),
  restore: (id, revision, memoryId) =>
    api(base(id) + '/memories/' + encodeURIComponent(memoryId) + '/restore', {
      principal: 'user',
      method: 'POST',
      body: { expected_revision: revision },
    }),
  price: (id) => api(base(id) + '/chat-price', { principal: 'kid' }),
  chat: (id, input, signal) =>
    api(base(id) + '/chat', { principal: 'kid', method: 'POST', body: input, signal }),
};
export const defaultBuddy: BuddyProfile = {
  name: 'Tuan Tuan',
  character: 'tuantuan',
  hat: 'cap',
  earStyle: 'up',
  room: 'cream',
  accessories: ['satchel'],
};
